/**
 * Player settings — persisted UI/camera preferences.
 *
 * This module is the single source of truth for `cameraInvert` (the
 * Google-Maps vs legacy drag direction). The skirmish setup screen reads
 * and writes it through here so the toggle in Settings and the toggle on
 * the setup screen can never disagree.
 */
export interface SettingsData {
  /** False (default): drag moves the world with the finger (Google Maps).
   *  True: legacy RTS feel — drag moves the camera with the finger. */
  cameraInvert: boolean;
  /** Camera pan/zoom speed multiplier (0.5 – 2.0, default 1). */
  cameraSpeed: number;
}

const KEY = "legionnaire-settings";

export const DEFAULT_SETTINGS: SettingsData = {
  cameraInvert: false,
  cameraSpeed: 1,
};

function clampSpeed(v: unknown): number {
  const n = typeof v === "number" && Number.isFinite(v) ? v : NaN;
  if (!Number.isFinite(n)) return DEFAULT_SETTINGS.cameraSpeed;
  return Math.min(2, Math.max(0.5, n));
}

/** Parse + validate unknown input into SettingsData (never throws). */
export function parseSettings(raw: unknown): SettingsData {
  const o = (typeof raw === "object" && raw !== null ? raw : {}) as Record<
    string,
    unknown
  >;
  return {
    cameraInvert:
      o.cameraInvert === true ? true : DEFAULT_SETTINGS.cameraInvert,
    cameraSpeed: clampSpeed(o.cameraSpeed),
  };
}

function readStorage(): SettingsData {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return parseSettings(JSON.parse(raw));
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function writeStorage(s: SettingsData): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* storage unavailable (private mode) — settings just don't persist */
  }
}

/**
 * Live settings object. `get()` returns a snapshot; `update()` merges a
 * partial change, persists it, and notifies subscribers.
 */
export class Settings {
  private data: SettingsData = readStorage();
  private listeners = new Set<(s: SettingsData) => void>();

  get(): SettingsData {
    return { ...this.data };
  }

  update(patch: Partial<SettingsData>): SettingsData {
    this.data = parseSettings({ ...this.data, ...patch });
    writeStorage(this.data);
    for (const fn of this.listeners) fn(this.get());
    return this.get();
  }

  /** Reload from storage (e.g. after another tab changed it). */
  reload(): SettingsData {
    this.data = readStorage();
    for (const fn of this.listeners) fn(this.get());
    return this.get();
  }

  onChange(fn: (s: SettingsData) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }
}

/** Process-wide shared settings instance. */
export const settings = new Settings();
